const QualityAssuranceUnit3Notes = `
<div class="space-y-8 animate-fadeIn">
    
    <!-- 1. Chapter Header -->
    <div>
        <h2 class="text-2xl font-black text-emerald-900 mb-3 border-b-2 border-slate-200 pb-2">Unit III: Quality Control & Good Laboratory Practices (GLP)</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            This unit covers two distinct but vital areas: the physical and chemical Quality Control (QC) tests required for primary and secondary packaging materials, and the strict framework of Good Laboratory Practices (GLP) that governs non-clinical safety studies.
        </p>
    </div>

    <!-- 2. Quality Control of Packaging Materials -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-emerald-800 mb-4 border-b border-slate-100 pb-2">1. Quality Control Tests for Packaging Materials</h3>
        <p class="text-[13px] text-slate-700 mb-4">Packaging materials are in direct contact with the drug (Primary) or protect the primary package (Secondary). They must be rigorously tested to ensure they do not react with the product or compromise its stability.</p>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <!-- Glass Containers -->
            <div class="bg-emerald-50 p-4 rounded-lg border border-emerald-100 shadow-sm">
                <h4 class="font-bold text-emerald-900 text-[15px] mb-2">A. Glass Containers</h4>
                <p class="text-[12px] text-slate-700 mb-2">Used for ampoules, vials, and bottles. Must be tested for chemical resistance and light protection.</p>
                <ul class="list-disc pl-4 text-[12px] text-slate-600 space-y-1">
                    <li><strong>Powdered Glass Test:</strong> Evaluates leaching of alkali from powdered glass.</li>
                    <li><strong>Water Attack Test:</strong> Specifically for Type II treated glass to check surface alkali release.</li>
                    <li><strong>Light Transmission Test:</strong> For amber-colored glass to ensure it blocks UV light (protects light-sensitive drugs).</li>
                </ul>
            </div>

            <!-- Plastic Containers -->
            <div class="bg-teal-50 p-4 rounded-lg border border-teal-100 shadow-sm">
                <h4 class="font-bold text-teal-900 text-[15px] mb-2">B. Plastic Containers</h4>
                <p class="text-[12px] text-slate-700 mb-2">Widely used but prone to leaching, permeation, and sorption.</p>
                <ul class="list-disc pl-4 text-[12px] text-slate-600 space-y-1">
                    <li><strong>Leakage Test:</strong> Filled containers are pressed or subjected to vacuum to ensure no leaks.</li>
                    <li><strong>Water Vapor Permeability:</strong> Checks if moisture can pass through the plastic over time.</li>
                    <li><strong>Extractable & Leachable Test:</strong> Identifies toxic chemicals migrating from plastic into the drug.</li>
                </ul>
            </div>

            <!-- Rubber Closures -->
            <div class="bg-blue-50 p-4 rounded-lg border border-blue-100 shadow-sm">
                <h4 class="font-bold text-blue-900 text-[15px] mb-2">C. Rubber Closures</h4>
                <p class="text-[12px] text-slate-700 mb-2">Used as stoppers in vials. Must allow needle puncture without shedding particles.</p>
                <ul class="list-disc pl-4 text-[12px] text-slate-600 space-y-1">
                    <li><strong>Fragmentation Test:</strong> Ensures rubber pieces don't fall into the vial when pierced by a syringe.</li>
                    <li><strong>Self-Sealability Test:</strong> Ensures the hole closes immediately after the needle is withdrawn, maintaining sterility.</li>
                    <li><strong>Physicochemical Tests:</strong> Testing the aqueous extract for pH shift, heavy metals, and turbidity.</li>
                </ul>
            </div>
        </div>

        <!-- Secondary Packaging -->
        <div class="bg-slate-50 p-4 rounded border border-slate-200 text-[13px] text-slate-700">
            <h4 class="font-bold text-emerald-900 mb-1">D. Secondary Packaging Materials (Cartons, Corrugated Boxes)</h4>
            <p class="text-[12px] text-slate-600">Tests include: <strong>Bursting Strength</strong>, <strong>Drop Test</strong> (evaluates transport durability), and <strong>Folding Endurance</strong> for paper board cartons.</p>
        </div>
    </div>

    <!-- 3. Good Laboratory Practices (GLP) -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-emerald-800 mb-3 border-b border-slate-100 pb-2">2. Good Laboratory Practices (GLP)</h3>
        <p class="text-[13px] text-slate-700 mb-4 leading-relaxed text-justify">
            GLP is a quality system concerned with the organizational process and the conditions under which <strong>non-clinical health and environmental safety studies</strong> are planned, performed, monitored, recorded, archived, and reported. Its goal is to ensure the uniformity, consistency, reliability, and integrity of chemical test data.
        </p>

        <!-- Organization and Personnel -->
        <div class="mb-5">
            <h4 class="text-[15px] font-bold text-emerald-900 mb-2">A. Organization and Personnel</h4>
            <ul class="list-disc pl-5 space-y-2 text-[13px] text-slate-600">
                <li><strong>Test Facility Management:</strong> Bears the ultimate responsibility for ensuring GLP compliance and establishing a Quality Assurance Unit (QAU).</li>
                <li><strong>Study Director:</strong> The single point of control for a study. Responsible for approving the protocol, overseeing the conduct of the study, and signing the final report.</li>
                <li><strong>Quality Assurance Unit (QAU):</strong> An independent body that audits studies and facilities to ensure management that GLP is being followed. They must not be involved in conducting the study.</li>
            </ul>
        </div>

        <!-- Facilities & Equipment -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div class="border border-slate-200 p-4 rounded-lg bg-slate-50">
                <h4 class="font-bold text-emerald-800 text-[14px] mb-2">B. Facilities & Equipment</h4>
                <p class="text-[12px] text-slate-600 mb-2">Facilities must be of suitable size and construction. Key requirements include:</p>
                <ul class="list-disc pl-4 text-[12px] text-slate-600 space-y-1">
                    <li>Separate areas for housing <em>Test Systems</em> (e.g., animals, cell cultures) to prevent cross-contamination.</li>
                    <li>Secure <strong>Archive facilities</strong> for storing raw data, reports, and specimens.</li>
                    <li>Equipment must be routinely calibrated, inspected, and maintained, with all actions strictly logged.</li>
                </ul>
            </div>
            <div class="border border-slate-200 p-4 rounded-lg bg-slate-50">
                <h4 class="font-bold text-emerald-800 text-[14px] mb-2">C. Test and Control Articles</h4>
                <p class="text-[12px] text-slate-600 mb-2">The chemical/drug being tested (Test Article) and the reference substance (Control Article) must be managed carefully:</p>
                <ul class="list-disc pl-4 text-[12px] text-slate-600 space-y-1">
                    <li>Proper characterization (identity, purity, stability).</li>
                    <li>Safe storage conditions to preserve stability.</li>
                    <li>Strict chain of custody and accountability logs for every mg used.</li>
                </ul>
            </div>
        </div>

        <!-- Protocols, Records and Disqualification -->
        <div class="bg-red-50 p-4 rounded-lg border border-red-100 shadow-sm text-[13px] text-slate-700">
            <h4 class="font-bold text-red-900 mb-2">D. Protocols, Records, and Disqualification</h4>
            <ul class="list-disc pl-5 space-y-2 text-[12px]">
                <li><strong>Protocol (Study Plan):</strong> A detailed, written plan that must be approved by the Study Director <em>before</em> the study begins. Any deviations must be documented and justified.</li>
                <li><strong>Records and Reports:</strong> All "Raw Data" (original worksheets, instrument printouts) must be signed, dated, and archived safely for a specified number of years.</li>
                <li><strong>Disqualification of Testing Facilities:</strong> If regulatory bodies (like FDA or OECD) find severe GLP non-compliance that compromises the data's integrity, the facility can be disqualified. This means their data will no longer be accepted for regulatory drug approvals.</li>
            </ul>
        </div>
    </div>

</div>
`;

export default QualityAssuranceUnit3Notes;